"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { AiChatSession, AiChatMessage, AiChatMode } from "@/types/ai-assistant";
import { aiAssistantRepository } from "@/repository/aiAssistantRepository";
import { AiMessageBubble } from "./AiMessageBubble";
import {
  formatSessionActivityAt,
  formatSessionActivityTitle,
} from "./aiChatSessionTime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  Sparkles,
  X,
  Send,
  Trash2,
  BookOpen,
  BarChart3,
  Loader2,
  Check,
  Plus,
  Maximize2,
  Minimize2,
} from "lucide-react";

interface AiChatPanelProps {
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onNavigate: () => void;
  onClose: () => void;
}

type View = "sessions" | "chat";
const DEFAULT_SESSION_TITLE = "新しいチャット";

export function AiChatPanel({
  isExpanded,
  onToggleExpanded,
  onNavigate,
  onClose,
}: AiChatPanelProps) {
  const location = useLocation();
  const [view, setView] = useState<View>("sessions");
  const [sessions, setSessions] = useState<AiChatSession[]>([]);
  const [activeSession, setActiveSession] = useState<AiChatSession | null>(
    null
  );
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [inputText, setInputText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isCreating, setIsCreating] = useState<AiChatMode | null>(null);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [isUpdatingTitle, setIsUpdatingTitle] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);
  const scrollBottomRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const isComposingMessageRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    scrollBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // セッション一覧を取得
  const loadSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      const data = await aiAssistantRepository.getSessions();
      setSessions(data);
    } catch {
      // エラー時は空のまま
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    setTitleDraft(activeSession?.title ?? "");
    setIsEditingTitle(false);
    setTitleError(null);
  }, [activeSession?.id]);

  useEffect(() => {
    if (!isEditingTitle) return;
    titleInputRef.current?.focus();
    titleInputRef.current?.select();
  }, [isEditingTitle]);

  // セッションを選択してメッセージを表示
  const openSession = async (session: AiChatSession) => {
    setActiveSession(session);
    setIsEditingTitle(false);
    setTitleError(null);
    setView("chat");
    try {
      const detail = await aiAssistantRepository.getSession(session.id);
      setMessages(detail.messages ?? []);
    } catch {
      setMessages([]);
    }
  };

  // 新しいセッションを作成して開く
  const createAndOpenSession = async (mode: AiChatMode) => {
    setIsCreating(mode);
    setErrorMsg(null);
    try {
      const session = await aiAssistantRepository.createSession(mode);
      setSessions((prev) => [session, ...prev]);
      setActiveSession(session);
      setIsEditingTitle(false);
      setTitleError(null);
      setMessages([]);
      setView("chat");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setErrorMsg(`チャットを開始できませんでした: ${msg}`);
    } finally {
      setIsCreating(null);
    }
  };

  // セッション削除
  const deleteSession = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await aiAssistantRepository.deleteSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      if (activeSession?.id === id) {
        setView("sessions");
        setActiveSession(null);
      }
    } catch {
      // エラー無視
    }
  };

  const startEditingTitle = () => {
    if (!activeSession) return;
    setTitleDraft(activeSession.title);
    setTitleError(null);
    setIsEditingTitle(true);
  };

  const cancelEditingTitle = () => {
    setTitleDraft(activeSession?.title ?? "");
    setTitleError(null);
    setIsEditingTitle(false);
  };

  const saveTitle = async () => {
    if (!activeSession || isUpdatingTitle) return;
    const title = titleDraft.trim();
    if (!title) {
      setTitleError("タイトルを入力してください");
      return;
    }
    if (title === activeSession.title) {
      setIsEditingTitle(false);
      setTitleError(null);
      return;
    }

    setIsUpdatingTitle(true);
    setTitleError(null);
    try {
      const updated = await aiAssistantRepository.updateSessionTitle(
        activeSession.id,
        title
      );
      setActiveSession((prev) =>
        prev?.id === activeSession.id ? { ...prev, title: updated.title } : prev
      );
      setSessions((prev) =>
        prev.map((session) =>
          session.id === activeSession.id
            ? { ...session, title: updated.title }
            : session
        )
      );
      setTitleDraft(updated.title);
      setIsEditingTitle(false);
    } catch {
      setTitleError("タイトルを変更できませんでした。もう一度お試しください");
    } finally {
      setIsUpdatingTitle(false);
    }
  };

  // メッセージ送信
  const sendMessage = async () => {
    if (!inputText.trim() || !activeSession || isSending) return;

    const userMessage: AiChatMessage = {
      id: `user-${Date.now()}`,
      sessionId: activeSession.id,
      role: "user",
      content: inputText.trim(),
      createdAt: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText("");
    setIsSending(true);

    try {
      const aiMessage = await aiAssistantRepository.sendMessage(
        activeSession.id,
        userMessage.content,
        { currentRoute: `${location.pathname}${location.search}` }
      );
      setMessages((prev) => [...prev, aiMessage]);

      const latestActivityAt = aiMessage.createdAt;
      setActiveSession((prev) =>
        prev ? { ...prev, updatedAt: latestActivityAt } : prev
      );
      setSessions((prev) =>
        prev
          .map((session) =>
            session.id === activeSession.id
              ? { ...session, updatedAt: latestActivityAt }
              : session
          )
          .sort(
            (left, right) =>
              new Date(right.updatedAt).getTime() -
              new Date(left.updatedAt).getTime()
          )
      );

      // セッションのタイトルを1つ目のメッセージから自動設定
      if (
        messages.length === 0 &&
        activeSession.title === DEFAULT_SESSION_TITLE
      ) {
        const title =
          userMessage.content.slice(0, 30) +
          (userMessage.content.length > 30 ? "…" : "");
        await aiAssistantRepository.updateSessionTitle(activeSession.id, title);
        setActiveSession((prev) => (prev ? { ...prev, title } : prev));
        setSessions((prev) =>
          prev.map((s) => (s.id === activeSession.id ? { ...s, title } : s))
        );
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          sessionId: activeSession.id,
          role: "assistant",
          content: "エラーが発生しました。もう一度お試しください。",
          createdAt: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      e.key !== "Enter" ||
      e.shiftKey ||
      e.nativeEvent.isComposing ||
      isComposingMessageRef.current ||
      e.nativeEvent.keyCode === 229
    ) {
      return;
    }

    e.preventDefault();
    void sendMessage();
  };

  return (
    <div className="bg-background flex h-full flex-col">
      {/* ヘッダー */}
      <header
        aria-label="AIチャットヘッダー"
        className={cn(
          "bg-primary text-primary-foreground border-b px-3 py-2",
          !isExpanded && "rounded-t-2xl"
        )}
      >
        <div className="flex min-h-9 items-center gap-1.5">
          {view === "chat" && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setView("sessions")}
              aria-label="会話一覧へ戻る"
              className="text-brand-strong hover:text-brand-strong dark:bg-background dark:hover:bg-accent size-10 shrink-0 rounded-full"
            >
              <ArrowLeft aria-hidden="true" className="size-4.5" />
            </Button>
          )}
          {view === "chat" && activeSession ? (
            isEditingTitle ? (
              <form
                className="flex min-w-0 flex-1 items-center gap-1"
                onSubmit={(event) => {
                  event.preventDefault();
                  void saveTitle();
                }}
              >
                <Input
                  ref={titleInputRef}
                  value={titleDraft}
                  onChange={(event) => {
                    setTitleDraft(event.target.value);
                    if (titleError) setTitleError(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      event.preventDefault();
                      cancelEditingTitle();
                    }
                  }}
                  aria-label="チャットタイトル"
                  aria-invalid={Boolean(titleError)}
                  aria-describedby={
                    titleError ? "ai-chat-title-error" : undefined
                  }
                  maxLength={200}
                  className="border-primary-foreground/40 bg-primary-foreground/10 text-primary-foreground focus-visible:border-primary-foreground focus-visible:ring-primary-foreground/30 h-8 min-w-0 px-2 text-sm font-semibold"
                />
                <Button
                  type="submit"
                  variant="outline"
                  size="icon"
                  disabled={isUpdatingTitle}
                  aria-label="タイトルを保存"
                  className="text-brand-strong hover:text-brand-strong dark:bg-background dark:hover:bg-accent size-8 shrink-0 rounded-full"
                >
                  {isUpdatingTitle ? (
                    <Loader2
                      aria-hidden="true"
                      className="size-4 animate-spin"
                    />
                  ) : (
                    <Check aria-hidden="true" className="size-4" />
                  )}
                </Button>
              </form>
            ) : (
              <button
                type="button"
                onClick={startEditingTitle}
                aria-label="チャットタイトルを変更"
                title="タイトルを変更"
                className="hover:bg-primary-foreground/15 focus-visible:ring-primary-foreground/50 flex min-w-0 flex-1 cursor-text items-center rounded-md px-1.5 py-1 text-left focus-visible:outline-none focus-visible:ring-2"
              >
                <span className="truncate text-base font-semibold">
                  {activeSession.title}
                </span>
              </button>
            )
          ) : (
            <span className="flex-1 truncate px-1.5 text-base font-semibold">
              AIアシスタント
            </span>
          )}
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={onToggleExpanded}
            aria-label={isExpanded ? "チャットを縮小" : "チャットを拡大"}
            aria-pressed={isExpanded}
            title={isExpanded ? "チャットを縮小" : "チャットを拡大"}
            className="text-brand-strong hover:text-brand-strong dark:bg-background dark:hover:bg-accent size-9 shrink-0 rounded-full"
          >
            {isExpanded ? (
              <Minimize2 aria-hidden="true" className="size-4" />
            ) : (
              <Maximize2 aria-hidden="true" className="size-4" />
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={onClose}
            aria-label="AIアシスタントを閉じる"
            className="text-brand-strong hover:text-brand-strong dark:bg-background dark:hover:bg-accent size-9 shrink-0 rounded-full"
          >
            <X aria-hidden="true" className="size-4" />
          </Button>
        </div>
        {titleError && (
          <p
            id="ai-chat-title-error"
            role="alert"
            className="bg-background/95 text-destructive mt-1 rounded-md px-2 py-1 text-xs"
          >
            {titleError}
          </p>
        )}
      </header>

      {/* セッション一覧ビュー */}
      {view === "sessions" && (
        <div className="relative flex flex-1 flex-col overflow-hidden">
          {/* 履歴 */}
          <div className="flex-1 overflow-y-auto pb-20">
            {isLoadingSessions ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="text-muted-foreground size-5 animate-spin" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-muted-foreground py-8 text-center text-sm">
                <Sparkles className="mx-auto mb-2 size-6 opacity-40" />
                <p>まだ会話がありません</p>
              </div>
            ) : (
              <div className="space-y-1 p-2">
                <p className="text-muted-foreground px-2 pb-1 text-xs font-medium">
                  最近の会話
                </p>
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className="hover:bg-accent group flex items-center rounded-lg transition-colors"
                  >
                    <button
                      type="button"
                      onClick={() => openSession(session)}
                      aria-label={`会話「${session.title}」を開く。${formatSessionActivityTitle(session.updatedAt)}`}
                      className="focus-visible:ring-primary/50 flex min-w-0 flex-1 items-center gap-2 rounded-lg px-3 py-2 text-left focus-visible:outline-none focus-visible:ring-2"
                    >
                      {session.mode === "analysis" ? (
                        <BarChart3
                          aria-hidden="true"
                          className="text-muted-foreground size-3.5 shrink-0"
                        />
                      ) : (
                        <BookOpen
                          aria-hidden="true"
                          className="text-muted-foreground size-3.5 shrink-0"
                        />
                      )}
                      <span className="flex-1 truncate text-sm">
                        {session.title}
                      </span>
                      <time
                        dateTime={session.updatedAt}
                        title={formatSessionActivityTitle(session.updatedAt)}
                        className="text-muted-foreground shrink-0 text-[11px] tabular-nums"
                      >
                        {formatSessionActivityAt(session.updatedAt)}
                      </time>
                    </button>
                    <button
                      onClick={(e) => deleteSession(e, session.id)}
                      aria-label={`「${session.title}」を削除`}
                      className="hover:text-destructive focus-visible:ring-destructive/40 mr-1 rounded p-1 opacity-70 transition-[color,opacity] focus-visible:outline-none focus-visible:ring-2 sm:opacity-0 sm:focus-visible:opacity-100 sm:group-hover:opacity-100"
                    >
                      <Trash2 aria-hidden="true" className="size-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {errorMsg && (
            <p
              role="alert"
              className="text-destructive bg-destructive/10 absolute inset-x-3 bottom-20 rounded-lg px-3 py-2 text-xs"
            >
              {errorMsg}
            </p>
          )}

          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="icon"
                disabled={isCreating !== null}
                aria-label="新しいチャットを開始"
                className="absolute bottom-4 right-4 size-12 rounded-full shadow-lg transition-[transform,box-shadow] hover:scale-105 hover:shadow-xl active:scale-95 motion-reduce:hover:scale-100 motion-reduce:active:scale-100"
              >
                {isCreating ? (
                  <Loader2 aria-hidden="true" className="size-5 animate-spin" />
                ) : (
                  <Plus aria-hidden="true" className="size-5" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="top"
              align="end"
              sideOffset={8}
              className="z-[60] w-64 p-1.5"
            >
              <DropdownMenuLabel className="text-muted-foreground px-2 py-1 text-xs">
                新しいチャット
              </DropdownMenuLabel>
              <DropdownMenuItem
                disabled={isCreating !== null}
                onSelect={() => void createAndOpenSession("guide")}
                className="items-start gap-3 px-2.5 py-2.5"
              >
                <BookOpen
                  aria-hidden="true"
                  className="text-primary mt-0.5 size-4"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">
                    操作ガイド
                  </span>
                  <span className="text-muted-foreground block text-xs">
                    管理画面の使い方を質問
                  </span>
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={isCreating !== null}
                onSelect={() => void createAndOpenSession("analysis")}
                className="items-start gap-3 px-2.5 py-2.5"
              >
                <BarChart3
                  aria-hidden="true"
                  className="text-primary mt-0.5 size-4"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">運営分析</span>
                  <span className="text-muted-foreground block text-xs">
                    データで経営状況を分析
                  </span>
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* チャットビュー */}
      {view === "chat" && activeSession && (
        <div className="flex flex-1 flex-col overflow-hidden">
          <ScrollArea className="flex-1 px-4 py-3">
            {messages.length === 0 ? (
              <div className="text-muted-foreground py-8 text-center">
                <Sparkles className="mx-auto mb-3 size-8 opacity-30" />
                <p className="text-sm font-medium">
                  {activeSession.mode === "analysis"
                    ? "運営分析モード"
                    : "操作ガイドモード"}
                </p>
                <p className=" text-xs opacity-70">
                  {activeSession.mode === "analysis"
                    ? "受講者数・出席率・コース稼働状況などを分析します"
                    : "管理画面の操作方法について何でも聞いてください"}
                </p>
              </div>
            ) : (
              messages.map((msg) => (
                <AiMessageBubble
                  key={msg.id}
                  message={msg}
                  onNavigate={onNavigate}
                />
              ))
            )}
            {isSending && (
              <div className="mb-3 flex items-center gap-2">
                <div className="mb-1 ml-1 flex items-center gap-1">
                  <Sparkles className="text-primary size-3" />
                </div>
                <div className="bg-muted flex items-center gap-1.5 rounded-2xl rounded-tl-sm px-4 py-2.5">
                  <span className="bg-muted-foreground/50 size-1.5 animate-bounce rounded-full [animation-delay:0ms]" />
                  <span className="bg-muted-foreground/50 size-1.5 animate-bounce rounded-full [animation-delay:150ms]" />
                  <span className="bg-muted-foreground/50 size-1.5 animate-bounce rounded-full [animation-delay:300ms]" />
                </div>
              </div>
            )}
            <div ref={scrollBottomRef} />
          </ScrollArea>

          {/* 入力エリア */}
          <div className="border-t px-3 py-3">
            <div className="flex items-end gap-2">
              <Textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onCompositionStart={() => {
                  isComposingMessageRef.current = true;
                }}
                onCompositionEnd={() => {
                  isComposingMessageRef.current = false;
                }}
                onKeyDown={handleKeyDown}
                placeholder="メッセージを入力…（Enterで送信）"
                className="max-h-[120px] min-h-[40px] resize-none rounded-xl text-sm"
                rows={1}
                disabled={isSending}
              />
              <Button
                size="icon"
                onClick={sendMessage}
                disabled={!inputText.trim() || isSending}
                aria-label="メッセージを送信"
                className="size-10 shrink-0 rounded-xl"
              >
                {isSending ? (
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                ) : (
                  <Send aria-hidden="true" className="size-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
